<script setup>
import { computed } from 'vue';
import AppMenuItem from './AppMenuItem.vue';
import { useAdminStatus } from '@/composables/useAdminStatus';

// Use the admin status composable for reactive updates with periodic checks
const { isAdmin } = useAdminStatus(300000); // Check every 5 minutes

// Keep backward compatibility with existing computed property
const isUserAdmin = computed(() => isAdmin.value);

// Define the complete menu structure
const baseModel = [
    {
        label: '',
        icon: 'pi pi-fw pi-cog',
        path: '/main',
        items: [
            {
                label: 'Dashboard',
                icon: 'pi pi-fw pi-home',
                to: '/'
            },
            {
                label: 'My Calendar',
                icon: 'pi pi-fw pi-calendar',
                to: '/calendar'
            },
            {
                label: 'My Court Days',
                icon: 'pi pi-fw pi-briefcase',
                to: '/court-days'
            },
            {
                label: 'Documents',
                icon: 'pi pi-fw pi-file',
                to: '/documents'
            },
            {
                label: 'Upload Documents',
                icon: 'pi pi-fw pi-cloud-upload',
                to: '/documents/upload-with-inbound'
            },
            {
                label: 'Contacts',
                icon: 'pi pi-fw pi-user',
                to: '/crm/contacts'
            },
            {
                label: 'Communications',
                icon: 'pi pi-fw pi-phone',
                to: '/crm/communications'
            },
            {
                label: 'Needs Review',
                icon: 'pi pi-fw pi-comments',
                to: '/campaign-responses/needs-review'
            },
            {
                label: 'Messages',
                icon: 'pi pi-fw pi-inbox',
                to: '/mail/messages'
            },
            {
                label: 'Account Settings',
                icon: 'pi pi-fw pi-cog',
                to: '/account/settings',
                isAdminOnly: true
            },
            {
                label: 'FAQ',
                icon: 'pi pi-fw pi-question-circle',
                to: '/faq'
            }
        ]
    },
    { separator: true },
    {
        label: 'Admin',
        icon: 'pi pi-fw pi-cog',
        path: '/admin',
        isAdminOnly: true,
        items: [
            {
                label: 'Upload iPhone Messages',
                icon: 'pi pi-fw pi-mobile',
                to: '/imazing/upload'
            },
            {
                label: 'Reactor Runs',
                icon: 'pi pi-fw pi-bolt',
                to: '/reactors/runs'
            },
            {
                // LAW-1140. A report nobody can find is a report nobody runs,
                // and this one is the point of the subpoena funnel rather than
                // a diagnostic — so it gets a top-level Admin entry rather than
                // living behind a URL somebody has to be told.
                label: 'Missing Subpoenas',
                icon: 'pi pi-fw pi-exclamation-triangle',
                to: '/admin/missing-subpoenas'
            },
            {
                label: 'Backlog',
                icon: 'pi pi-fw pi-clock',
                path: '/backlog',
                items: [
                    {
                        label: 'CRM Backlog',
                        icon: 'pi pi-fw pi-list',
                        to: '/admin/crm-backlog'
                    }
                ]
            },
            {
                label: 'CRM',
                icon: 'pi pi-fw pi-users',
                path: '/crm',
                items: [
                    {
                        label: 'Providers',
                        icon: 'pi pi-fw pi-building',
                        to: '/crm/providers'
                    },
                    {
                        label: 'Cases',
                        icon: 'pi pi-fw pi-briefcase',
                        to: '/admin/crm-cases'
                    }
                ]
            },
            {
                label: 'Mail',
                icon: 'pi pi-fw pi-inbox',
                path: '/mail',
                items: [
                    {
                        label: 'Mail Providers',
                        icon: 'pi pi-fw pi-server',
                        to: '/mail/providers'
                    }
                ]
            },
            {
                label: 'Fax',
                icon: 'pi pi-fw pi-print',
                path: '/fax',
                items: [
                    {
                        label: 'Providers',
                        icon: 'pi pi-fw pi-server',
                        to: '/fax/providers'
                    }
                ]
            },
            {
                label: 'Scan',
                icon: 'pi pi-fw pi-camera',
                path: '/scan',
                items: [
                    {
                        label: 'Providers',
                        icon: 'pi pi-fw pi-server',
                        to: '/scan/providers'
                    }
                ]
            },
            {
                label: 'Inbound',
                icon: 'pi pi-fw pi-inbox',
                path: '/inbound',
                items: [
                    {
                        label: 'Manage Inbounds',
                        icon: 'pi pi-fw pi-inbox',
                        to: '/inbounds'
                    }
                ]
            },
            {
                label: 'Upload',
                icon: 'pi pi-fw pi-cloud-upload',
                to: '/document-upload'
            },
            {
                label: 'Reports',
                icon: 'pi pi-fw pi-chart-line',
                path: '/reports',
                items: [
                    {
                        label: 'Daily Analytics',
                        icon: 'pi pi-fw pi-calendar',
                        to: '/reports/daily'
                    },
                    {
                        label: 'Case Assignment',
                        icon: 'pi pi-fw pi-users',
                        to: '/reports/case-assignment'
                    }
                ]
            },
            {
                label: 'Notifications',
                icon: 'pi pi-fw pi-bell',
                path: '/notifications',
                items: [
                    {
                        label: 'View Notifications',
                        icon: 'pi pi-fw pi-list',
                        to: '/admin/notifications'
                    },
                    {
                        label: 'Send Notification',
                        icon: 'pi pi-fw pi-send',
                        to: '/admin/notifications/send'
                    },
                    {
                        label: 'Check Missed',
                        icon: 'pi pi-fw pi-exclamation-triangle',
                        to: '/admin/notifications/check-missed'
                    }
                ]
            },
            {
                // The controlled WCM rollout was previously reachable only
                // through the fourth step of the generic seven-step campaign
                // builder. Operations staff run this weekly, so it gets its
                // own top-level Admin entry pointing at the focused page.
                label: 'WCM Check-ins',
                icon: 'pi pi-fw pi-heart',
                to: '/admin/wcm-checkins'
            },
            {
                label: 'Campaigns',
                icon: 'pi pi-fw pi-megaphone',
                to: '/admin/campaigns'
            },
            {
                label: 'Marketing Dashboard',
                icon: 'pi pi-fw pi-chart-bar',
                to: '/marketing/dashboard'
            },
            {
                label: 'Ad Management',
                icon: 'pi pi-fw pi-google',
                path: '/ad-management',
                items: [
                    {
                        label: 'Campaign Overview',
                        icon: 'pi pi-fw pi-chart-pie',
                        to: '/ad-management'
                    },
                    {
                        label: 'Pending Approvals',
                        icon: 'pi pi-fw pi-clock',
                        to: '/ad-management/approvals'
                    },
                    {
                        label: 'Decision Log',
                        icon: 'pi pi-fw pi-list',
                        to: '/ad-management/decisions'
                    },
                    {
                        label: 'Anomalies',
                        icon: 'pi pi-fw pi-bell',
                        to: '/ad-management/anomalies'
                    },
                    {
                        label: 'Marketing Brief',
                        icon: 'pi pi-fw pi-file-edit',
                        to: '/ad-management/brief'
                    },
                    {
                        label: 'Safeguards',
                        icon: 'pi pi-fw pi-shield',
                        to: '/ad-management/settings/safeguards',
                        isAdminOnly: true
                    },
                    {
                        label: 'Policies',
                        icon: 'pi pi-fw pi-cog',
                        to: '/ad-management/settings/policies',
                        isAdminOnly: true
                    },
                    {
                        label: 'Role Assignments',
                        icon: 'pi pi-fw pi-users',
                        to: '/ad-management/settings/role-assignments',
                        isAdminOnly: true
                    }
                ]
            }
        ]
    }
];

// Helper function to recursively filter menu items
const filterMenuItems = (items) => {
    return items.filter((item) => {
        // If item is admin-only, check if user is admin
        if (item.isAdminOnly && !isUserAdmin.value) {
            return false;
        }

        // If item has children, filter them recursively
        if (item.items && item.items.length > 0) {
            item.items = filterMenuItems(item.items);
        }

        return true;
    });
};

// Filter menu items based on admin status - this will be reactive
const model = computed(() => {
    // Deep clone the baseModel to avoid mutating the original
    const clonedModel = JSON.parse(JSON.stringify(baseModel));
    return filterMenuItems(clonedModel);
});
</script>

<template>
    <ul class="layout-menu">
        <template v-for="(item, index) in model" :key="item.label + '_' + (item.to || item.path || index)">
            <AppMenuItem v-if="!item.separator" :item="item" root />

            <li v-else class="menu-separator" />
        </template>
    </ul>
</template>
